import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore'
import { AlertModal, ConfirmModal } from '../AlertModal'
import styles from './CommunityPage.module.css'
import Header from '../Layout/Header'
import profileIcon from '../../assets/icons/profileIconBig.svg'
import communityIcon from '../../assets/icons/communityBlack.svg'
import CreatePostModal from './CreatePostModal'

const CommunityPage = () => {
  const location = useLocation()
  const initialTab = location.state?.tab === 'myPosts' ? 'myPosts' : 'all'
  const [activeTab, setActiveTab] = useState(initialTab) // 'all', 'myPosts'
  const [selectedPost, setSelectedPost] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [hotSlideIndex, setHotSlideIndex] = useState(0)
  const [showCreatePostModal, setShowCreatePostModal] = useState(false)
  const [showEditPostModal, setShowEditPostModal] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [postToDelete, setPostToDelete] = useState(null)
  const [showDeleteCommentConfirm, setShowDeleteCommentConfirm] = useState(false)
  const [commentToDelete, setCommentToDelete] = useState(null)

  const [isLiked, setIsLiked] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [likeLoading, setLikeLoading] = useState(false)

  const [comments, setComments] = useState({}) 
  const [commentPage, setCommentPage] = useState({})
  const [commentText, setCommentText] = useState('') 
  const [replyText, setReplyText] = useState({}) 
  const [replyingTo, setReplyingTo] = useState(null) 
  const [commentMeta, setCommentMeta] = useState({})
  
  const itemsPerSlide = 4
  const COMMENTS_PER_PAGE = 10

  const [hotPosts, setHotPosts] = useState([]);
  const [userProfileImage, setUserProfileImage] = useState(null);

  const loaderRef = useRef(null);
  const myLoaderRef = useRef(null);
  const menuRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartTime = useRef(null);
  const isDragging = useRef(false);

  const [posts, setPosts] = useState([]);
  const [hasNext, setHasNext] = useState(true);
  const [lastCreatedAt, setLastCreatedAt] = useState(null);
  const [lastArticleId, setLastArticleId] = useState(null);
  const [isFetchingPosts, setIsFetchingPosts] = useState(false);

  const [myPosts, setMyPosts] = useState([]);
  const [myHasNext, setMyHasNext] = useState(true);
  const [myLastCreatedAt, setMyLastCreatedAt] = useState(null);
  const [myLastArticleId, setMyLastArticleId] = useState(null);
  const [isFetchingMyPosts, setIsFetchingMyPosts] = useState(false);
  const [showPostMenu, setShowPostMenu] = useState(false);

  useEffect(() => {
    if (location.state?.tab === 'myPosts') {
      setActiveTab('myPosts')
    }
    if (location.state?.showCreatePostModal) {
      setShowCreatePostModal(true)
      window.history.replaceState({}, '', location.pathname)
    }
  }, [location.state])

  const fetchPosts = async (reset = false) => {
    if (isFetchingPosts && !reset) return
    
    try {
      setIsFetchingPosts(true)
      const token = getAccessToken();
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }
      
      const limit = 20; 
      const cursorLastCreatedAt = reset ? null : lastCreatedAt
      const cursorLastArticleId = reset ? null : lastArticleId

      const params = { limit }

      if (cursorLastCreatedAt) {
        params.lastCreatedAt = cursorLastCreatedAt
      }
      if (cursorLastArticleId) {
        params.lastArticleId = cursorLastArticleId
      }

      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/articles/infinite-scrolls`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = response.data?.data || response.data;
      const newPosts = data.articles || [];
      const scrollInfo = data.scrollInfo || {};

      setPosts(prevPosts => reset ? newPosts : [...prevPosts, ...newPosts]); 
      setHasNext(scrollInfo.hasNext ?? false); 
      setLastCreatedAt(scrollInfo.lastCreatedAt ?? null); 
      setLastArticleId(scrollInfo.lastArticleId ?? null); 
    } catch (error) {
      console.error('게시글을 불러오는 데 실패했습니다:', error);
    } finally {
      setIsFetchingPosts(false)
    }
  };

  const fetchMyPosts = async (reset = false) => {
    if (isFetchingMyPosts && !reset) return

    try {
      setIsFetchingMyPosts(true)
      const token = getAccessToken();
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }

      const limit = 20;
      const cursorLastCreatedAt = reset ? null : myLastCreatedAt
      const cursorLastArticleId = reset ? null : myLastArticleId

      const params = { limit }

      if (cursorLastCreatedAt) {
        params.lastCreatedAt = cursorLastCreatedAt
      }
      if (cursorLastArticleId) {
        params.lastArticleId = cursorLastArticleId
      }

      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/articles/my/infinite-scrolls`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = response.data?.data || response.data;
      const newPosts = data.articles || [];
      const scrollInfo = data.scrollInfo || {};

      setMyPosts(prev => reset ? newPosts : [...prev, ...newPosts]);
      setMyHasNext(scrollInfo.hasNext ?? false);
      setMyLastCreatedAt(scrollInfo.lastCreatedAt ?? null);
      setMyLastArticleId(scrollInfo.lastArticleId ?? null);
    } catch (error) {
      console.error('내 갤러리를 불러오는 데 실패했습니다:', error);
    } finally {
      setIsFetchingMyPosts(false)
    }
  };

  const fetchPopularPosts = async () => {
    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')
  
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/articles/popular`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
  
      const apiData = response.data?.data?.popularArticles ?? []
  
      const mapped = apiData.map((item, index) => ({
        id: item.articleId,
        imageUrl: item.articleImageUrl,
        title: item.title,
        author: item.authorName,
        authorImageUrl: item.authorImageUrl,
        views: item.viewCount,
        likes: item.likeCount,
        comments: item.commentCount,
        createdAt: item.createdAt,
        rank: index + 1,
      }))
  
      setHotPosts(mapped)
    } catch (err) {
      console.error('인기 게시글을 불러오는 데 실패했습니다:', err)
    }
  }

  const fetchLikeStatus = async (articleId) => {
    if (!articleId) return
    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/article-likes/articles/${articleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      
      const isLiked = response.data?.data?.isLiked ?? false
      setIsLiked(isLiked)
    } catch (error) {
      console.error('좋아요 상태 조회 실패:', error)
      setIsLiked(false)
    }
  }

  const toggleLike = async () => {
    if (!selectedPost || likeLoading) return

    const articleId = selectedPost.articleId || selectedPost.id
    const previousLikedState = isLiked
    const previousLikeCount = likeCount

    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      setLikeLoading(true)

      if (isLiked) {
        await axios.delete(
          `${import.meta.env.VITE_API_BASE}/api/article-likes/articles/${articleId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        setIsLiked(false)
        setLikeCount((prev) => Math.max(prev - 1, 0))

        await fetchLikeStatus(articleId)
        await fetchLikeCount(articleId)
      } else {
        await axios.post(
          `${import.meta.env.VITE_API_BASE}/api/article-likes/articles/${articleId}`,
          {}, 
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        setIsLiked(true)
        setLikeCount((prev) => prev + 1)

        await fetchLikeStatus(articleId)
        await fetchLikeCount(articleId)
      }
    } catch (error) {
      console.error('좋아요 처리 실패:', error)
      setIsLiked(previousLikedState)
      setLikeCount(previousLikeCount)
    } finally {
      setLikeLoading(false)
    }
  }

  const fetchLikeCount = async (articleId) => {
    if (!articleId) return
    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/article-likes/articles/${articleId}/count`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const likeCountFromApi =
        response.data?.data?.likeCount ??
        0

      setLikeCount(likeCountFromApi || 0)
    } catch (error) {
      console.error('좋아요 수 조회 실패:', error)
      setLikeCount(0)
    }
  }


  const fetchArticleDetail = async (articleId) => {
    if (!articleId) return
    try {
      setDetailLoading(true)
      setDetailError(null)
      setSelectedPost(null)

      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/articles/${articleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      const article = response.data?.data
      setSelectedPost(article)

      const id = article.articleId || article.id

      await fetchLikeCount(id)
      await fetchComments(id, 0)
      await fetchLikeStatus(article.articleId || article.id)

    } catch (error) {
      console.error('게시글 상세 조회 실패:', error)
      setDetailError('게시글을 불러오는 데 실패했습니다.')
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => {
    fetchPosts(true);
    fetchPopularPosts();
    const fetchUserProfile = async () => {
      try {
        const token = getAccessToken();
        if (!token) return;
        const response = await axios.get(
          `${import.meta.env.VITE_API_BASE}/api/user/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUserProfileImage(response.data?.data?.profileImageUrl || null);
      } catch (error) {
        console.error('유저 프로필 정보를 가져오지 못했습니다.', error);
      }
    };
    fetchUserProfile();
    
    // URL에서 게시글 ID 읽어서 상세 조회
    const urlParams = new URLSearchParams(window.location.search)
    const articleId = urlParams.get('articleId')
    if (articleId) {
      fetchArticleDetail(parseInt(articleId))
    }
    
    // 브라우저 뒤로가기/앞으로가기 처리
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const articleId = urlParams.get('articleId')
      if (articleId) {
        fetchArticleDetail(parseInt(articleId))
      } else {
        handleBackToList()
      }
    }
    
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'myPosts' && myPosts.length === 0) {
      fetchMyPosts(true);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!loaderRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (!first.isIntersecting) return

        if (activeTab === 'all' && hasNext && !isFetchingPosts) {
          fetchPosts()
        }
      },
      {
        root: null,  
        rootMargin: '0px 0px 200px 0px',  
        threshold: 0.1,
      }
    )

    observer.observe(loaderRef.current)

    return () => {
      observer.disconnect()
    }
  }, [activeTab, hasNext, isFetchingPosts])

  useEffect(() => {
    if (!myLoaderRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (!first.isIntersecting) return

        if (activeTab === 'myPosts' && myHasNext && !isFetchingMyPosts) {
          fetchMyPosts()
        }
      },
      {
        root: null,
        rootMargin: '0px 0px 200px 0px',
        threshold: 0.1,
      }
    )

    observer.observe(myLoaderRef.current)

    return () => {
      observer.disconnect()
    }
  }, [activeTab, myHasNext, isFetchingMyPosts])


  const handlePostCardClick = (post) => {
    const articleId = post?.articleId || post?.id
    if (!articleId) return
    // URL에 게시글 ID 추가
    window.history.pushState({}, '', `?articleId=${articleId}`)
    fetchArticleDetail(articleId)
    setShowPostMenu(false)
  }

  const handleBackToList = async () => {
    // URL에서 게시글 ID 제거
    window.history.pushState({}, '', window.location.pathname)
    setSelectedPost(null)
    setDetailError(null)
    setShowPostMenu(false)
    setIsLiked(false)
    setLikeCount(0)

    await fetchPosts(true)
    await fetchMyPosts(true)
    await fetchPopularPosts()
  }

  //댓글 트리 생성....
  const buildCommentTree = (commentList) => {
    const roots = []
    const map = new Map()
  
    // 1) depth=1 루트만 먼저 만든다.
    commentList.forEach(c => {
      if (c.depth === 1) {
        const node = {
          id: c.commentId,
          author: c.userName,
          content: c.deleted ? '삭제된 댓글입니다.' : c.content,
          deleted: c.deleted || false,
          time: new Date(c.createdAt).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' }),
          profile: c.userProfileUrl,
          isMine: c.isMine,
          path: c.path,
          replies: []
        }
        roots.push(node)
        map.set(c.path, node)
      }
    })
  
    // 2) depth=2는 부모 path만 잘라 붙임.
    commentList.forEach(c => {
      if (c.depth === 2) {
        const parentPath = c.path.slice(0, c.path.length - 5)
        const parent = map.get(parentPath)
  
        const reply = {
          id: c.commentId,
          author: c.userName,
          content: c.deleted ? '삭제된 댓글입니다.' : c.content,
          deleted: c.deleted || false,
          time: new Date(c.createdAt).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' }),
          profile: c.userProfileUrl,
          isMine: c.isMine,
          path: c.path
        }
  
        if (parent) {
          parent.replies.push(reply)
        } else {
          // fallback (부모 못 찾으면 그냥 루트처럼)
          roots.push(reply)
        }
      }
    })
  
    return roots
  }

  //댓글 fetch
  const fetchComments = async (articleId, page = 0) => {
    if (!articleId && articleId !== 0) return
  
    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')
  
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/comments`,
        {
          params: {
            articleId,
            page,             
            size: COMMENTS_PER_PAGE,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
  
      const data = response.data?.data
      const rawComments = data?.comments || []
      const pageInfo = data?.pageInfo || {}
  
      const treeComments = buildCommentTree(rawComments)
  
      setComments(prev => ({
        ...prev,
        [articleId]: treeComments,
      }))
  
      setCommentPage(prev => ({
        ...prev,
        [articleId]: (pageInfo.pageNumber ?? page) + 1, 
      }))
  
      setCommentMeta(prev => ({
        ...prev,
        [articleId]: {
          totalPages: pageInfo.totalPages ?? 1,
          totalElements: pageInfo.totalElements ?? treeComments.length,
          pageSize: pageInfo.pageSize ?? COMMENTS_PER_PAGE,
        },
      }))
    } catch (error) {
      console.error('댓글 목록 조회 실패:', error)
    }
  }
  
  const handleCommentSubmit = async (e) => {
    e?.preventDefault()
    if (!commentText.trim() || !selectedPost) return

    const postId = selectedPost.id || selectedPost.articleId

    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      await axios.post(
        `${import.meta.env.VITE_API_BASE}/api/comments`,
        {
          articleId: postId,
          content: commentText.trim(),
          parentPath: null,         
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setCommentText('')

      await fetchComments(postId, 0)
      setCommentPage(prev => ({ ...prev, [postId]: 1 }))
    } catch (error) {
      console.error('댓글 작성 실패:', error)
      setAlertMessage('댓글 작성에 실패했습니다. 잠시 후 다시 시도해주세요.')
      setShowAlert(true)
    }
  }

  const handleReplySubmit = async (commentId) => {
    if (!replyText[commentId]?.trim() || !selectedPost) return

    const postId = selectedPost.id || selectedPost.articleId
    const content = replyText[commentId].trim()

    const postComments = comments[postId] || []
    const parentComment = postComments.find(c => c.id === commentId)

    if (!parentComment || !parentComment.path) {
      console.error('부모 댓글의 path 를 찾을 수 없습니다.')
      return
    }

    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      await axios.post(
        `${import.meta.env.VITE_API_BASE}/api/comments`,
        {
          articleId: postId,
          content,
          parentPath: parentComment.path,   
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setReplyText(prev => ({ ...prev, [commentId]: '' }))
      setReplyingTo(null)

      await fetchComments(postId, 0)
      setCommentPage(prev => ({ ...prev, [postId]: 1 }))
    } catch (error) {
      console.error('대댓글 작성 실패:', error)
      setAlertMessage('대댓글 작성에 실패했습니다. 잠시 후 다시 시도해주세요.')
      setShowAlert(true)
    }
  }


  const handleReplyClick = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId)
    if (replyingTo !== commentId) {
      setReplyText(prev => ({ ...prev, [commentId]: '' }))
    }
  }

  const handleDeleteComment = (commentId) => {
    if (!selectedPost) return
    setCommentToDelete(commentId)
    setShowDeleteCommentConfirm(true)
  }

  const confirmDeleteComment = async () => {
    if (!commentToDelete || !selectedPost) return

    const postId = selectedPost.id || selectedPost.articleId
    const currentPage = commentPage[postId] || 1

    try {
      const token = getAccessToken()
      if (!token) throw new Error('로그인 토큰이 없습니다.')

      await axios.delete(
        `${import.meta.env.VITE_API_BASE}/api/comments/${commentToDelete}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const totalElements = (commentMeta[postId]?.totalElements ?? 0) - 1
      const remainingElements = Math.max(totalElements, 0)
      const newTotalPages = Math.max(1, Math.ceil(remainingElements / COMMENTS_PER_PAGE))
      const targetPageNumber = Math.min(currentPage, newTotalPages)
      const targetPageIndex = Math.max(0, targetPageNumber - 1)

      await fetchComments(postId, targetPageIndex)
      setCommentPage(prev => ({ ...prev, [postId]: targetPageNumber }))
      setAlertMessage('댓글이 삭제되었습니다.')
      setShowAlert(true)
      setCommentToDelete(null)
    } catch (error) {
      console.error('댓글 삭제 실패:', error)
      if (error.response?.status === 404) {
        setAlertMessage('댓글을 찾을 수 없습니다.')
      } else {
        setAlertMessage('댓글 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.')
      }
      setShowAlert(true)
      setCommentToDelete(null)
    }
  }

  useEffect(() => {
    if (selectedPost && myPosts.length === 0 && !isFetchingMyPosts) {
      fetchMyPosts(true)
    }
  }, [selectedPost])

  useEffect(() => {
    if (!showPostMenu) return

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowPostMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPostMenu])

  const isSelectedPostMine = useMemo(() => {
    if (!selectedPost) return false
    if (selectedPost.isMine || selectedPost.isAuthor || selectedPost.mine) return true
    const postId = selectedPost.id || selectedPost.articleId
    if (!postId) return false
    return myPosts.some(post => (post.articleId || post.id) === postId)
  }, [selectedPost, myPosts])

  const handleEditPost = () => {
    setShowPostMenu(false)
    setShowEditPostModal(true)
  }

  const handleUpdatePost = async (postData) => {
    if (!selectedPost) return

    try {
      const token = getAccessToken()
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }

      const postId = selectedPost.id || selectedPost.articleId

      await axios.patch(
        `${import.meta.env.VITE_API_BASE}/api/articles/${postId}`,
        {
          title: postData.title,
          content: postData.content,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setAlertMessage('게시글이 수정되었습니다!')
      setShowAlert(true)
      setShowEditPostModal(false)
      
      await fetchArticleDetail(postId)
    } catch (error) {
      console.error('게시글 수정 실패:', error)
      setAlertMessage('게시글 수정에 실패했습니다.')
      setShowAlert(true)
      throw error
    }
  }

  const handleDeletePost = (post) => {
    setPostToDelete(post)
    setShowDeleteConfirm(true)
    setShowPostMenu(false)
  }

  const confirmDeletePost = async () => {
    if (!postToDelete) return

    const postId = postToDelete?.articleId || postToDelete?.id
    if (!postId) return

    try {
      const token = getAccessToken()
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }

      await axios.delete(
        `${import.meta.env.VITE_API_BASE}/api/articles/${postId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      setAlertMessage('게시글이 삭제되었습니다.')
      setShowAlert(true)
      setShowPostMenu(false)
      setPostToDelete(null)
      // URL 초기화 및 목록으로 돌아가기
      await handleBackToList()
    } catch (error) {
      console.error('게시글 삭제 실패:', error)
      setAlertMessage('게시글 삭제에 실패했습니다.')
      setShowAlert(true)
      setPostToDelete(null)
    }
  }

  const handleCommentPageChange = (postId, newPage) => {
    if (!postId) return
    const meta = commentMeta[postId]
    const totalPages = meta?.totalPages || 1
  
    if (newPage < 1 || newPage > totalPages) return
  
    fetchComments(postId, newPage - 1)
  }

  const formatDateTime = (value) => {
    if (!value) return '-'
    try {
      return new Date(value).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return value
    }
  }

  const handleCreatePost = async (postData) => {
    try {
      if (!postData.imageFile) {
        setAlertMessage('이미지를 업로드해주세요.')
        setShowAlert(true)
        return
      }

      const token = getAccessToken()
      if (!token) {
        throw new Error('로그인 토큰이 없습니다.')
      }
      
      const mimeType = postData.imageFile.type || 'image/png'

      const response = await axios.post(`${import.meta.env.VITE_API_BASE}/api/s3/presigned-url`,
        { mimeType },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const presignedUrl = response.data?.data?.presignedUrl
      if (!presignedUrl) throw new Error('presignedUrl이 응답에 없습니다.')

      const imageUrl = presignedUrl.split('?')[0]

      await axios.put(presignedUrl, postData.imageFile, {
        headers: { 'Content-Type': mimeType }
      })

      const articleRes = await axios.post(
        `${import.meta.env.VITE_API_BASE}/api/articles`,
        {
          title: postData.title,
          content: postData.content,
          imageUrl: imageUrl, 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setAlertMessage('게시글 작성이 완료되었습니다!')
      setShowAlert(true)
      setShowCreatePostModal(false)
      setActiveTab('all')
      await fetchPosts(true)
      await fetchMyPosts(true)

    } catch (error) {
      console.error('게시글 작성 실패:', error)
      setAlertMessage('게시글 작성에 실패했습니다.')
      setShowAlert(true)
      throw error
    }
  }

  const handlePrevSlide = () => {
    setHotSlideIndex(prev => Math.max(0, prev - 1))
  }

  const handleNextSlide = () => {
    const maxIndex = Math.ceil(hotPosts.length / itemsPerSlide) - 1
    setHotSlideIndex(prev => Math.min(maxIndex, prev + 1))
  }

  const handleTouchStart = (e) => {
    const touch = e.touches ? e.touches[0] : e
    touchStartX.current = touch.clientX
    touchStartTime.current = Date.now()
    isDragging.current = false
  }

  const handleMouseDown = (e) => {
    handleTouchStart(e)
  }

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return
    isDragging.current = true
  }

  const handleMouseMove = (e) => {
    if (touchStartX.current === null) return
    isDragging.current = true
  }

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return

    const touch = e.changedTouches ? e.changedTouches[0] : e
    const touchEndX = touch.clientX
    const touchEndTime = Date.now()
    
    const deltaX = touchEndX - touchStartX.current
    const deltaTime = touchEndTime - (touchStartTime.current || touchEndTime)
    
    const minSwipeDistance = 50
    const maxSwipeTime = 500
    
    if (Math.abs(deltaX) >= minSwipeDistance && deltaTime <= maxSwipeTime && isDragging.current) {
      if (deltaX > 0) {
        handlePrevSlide()
      } else {
        handleNextSlide()
      }
    }
    
    touchStartX.current = null
    touchStartTime.current = null
    isDragging.current = false
  }

  const handleMouseUp = (e) => {
    handleTouchEnd(e)
  }

  const totalSlides = hotPosts.length > 0
    ? Math.ceil(hotPosts.length / itemsPerSlide)
    : 1

  if (detailLoading) {
    return (
      <>
        <Header />
        <div className={`${styles.container} ${styles.detailContainer}`}>
          <div className={styles.detailStatus}>
            <p>게시글을 불러오는 중입니다...</p>
          </div>
        </div>
      </>
    )
  }

  if (detailError && !selectedPost) {
    return (
      <>
        <Header />
        <div className={`${styles.container} ${styles.detailContainer}`}>
          <div className={styles.detailStatus}>
            <p>{detailError}</p>
            <button className={styles.backButton} onClick={() => setDetailError(null)}>
              다시 시도
            </button>
          </div>
        </div>
      </>
    )
  }

  // 상세 페이지
  if (selectedPost) {
    const detailPostId = selectedPost.id || selectedPost.articleId
    const postComments = comments[detailPostId] || []
    const currentCommentPage = commentPage[detailPostId] || 1
    const meta = commentMeta[detailPostId]
    const totalCommentPages = meta?.totalPages || 1
    const totalComments = meta?.totalElements ?? postComments.length

    const paginatedComments = postComments

    return (
      <>
        <Header />
        <div className={`${styles.container} ${styles.detailContainer}`}>
          <div className={styles.detailPage}>
            <button className={styles.backButton} onClick={handleBackToList}>
              <i className="fas fa-arrow-left"></i>
              페이지로 돌아가기
            </button>

            <div className={styles.detailContent}>
              <div className={styles.detailImage}>
                {/* 실제 그림 이미지 영역 */}
                <div className={styles.drawingDisplay}>
                  {selectedPost.imageUrl ? (
                    <img src={selectedPost.imageUrl} alt={selectedPost.title || '작품'} />
                  ) : (
                    <div className={styles.placeholder}>
                      
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.detailInfo}>
                <div className={styles.detailInfoHeader}>
                  <h1 className={styles.detailTitle}>{selectedPost.title || `작품 #${selectedPost.articleId || selectedPost.id}`}</h1>
                  <div className={styles.detailActions}>
                    {isSelectedPostMine && (
                      <div className={styles.postMenu} ref={menuRef}>
                        <button 
                          type="button"
                          className={styles.menuButton}
                          onClick={() => setShowPostMenu(prev => !prev)}
                        >
                          <i className="fas fa-ellipsis-h"></i>
                        </button>
                        {showPostMenu && (
                          <div className={styles.menuDropdown}>
                            <button 
                              type="button" 
                              className={styles.menuItem}
                              onClick={handleEditPost}
                            >
                              게시글 수정
                            </button>
                            <button 
                              type="button" 
                              className={`${styles.menuItem} ${styles.deleteItem}`}
                              onClick={() => handleDeletePost(selectedPost)}
                            >
                              게시글 삭제
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <div className={styles.likeButton}>
                      <button 
                        className={`${styles.heartBtn} ${isLiked ? styles.liked : ''}`}
                        onClick={toggleLike}
                        disabled={likeLoading}
                      >
                        <i className={isLiked ? "fas fa-heart" : "far fa-heart"}></i>
                        <span>{likeCount}</span>
                      </button>
                    </div>
                  </div>
                </div>
                {selectedPost.content && (
                  <p className={styles.detailDescription}>{selectedPost.content}</p>
                )}
                {!selectedPost.content && selectedPost.description && (
                  <p className={styles.detailDescription}>{selectedPost.description}</p>
                )}
                <div className={styles.detailMeta}>
                  <span>작성자 : {selectedPost.username || selectedPost.authorName || selectedPost.author || '익명'}</span>
                  <span>작성일 : {formatDateTime(selectedPost.createdAt)}</span>
                  <span>조회수 : {selectedPost.viewCount || selectedPost.views || 0}</span>
                </div>

                <div className={styles.commentsSection}>
                  <h3>댓글 {totalComments}</h3>
                  <form className={styles.commentInput} onSubmit={handleCommentSubmit}>
                    <div className={styles.commentAvatar}>
                      <img src={userProfileImage || profileIcon} alt="profile" />
                    </div>
                    <input 
                      type="text" 
                      placeholder="댓글 달기" 
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleCommentSubmit()
                        }
                      }}
                    />
                    <button 
                      type="submit"
                      className={styles.commentSubmit}
                      disabled={!commentText.trim()}
                    >
                      등록
                    </button>
                  </form>

                  <div className={styles.commentsList}>
                    {postComments.length > 0 ? (
                      paginatedComments.map((comment) => (
                      <div key={comment.id} className={styles.comment}>
                        <div className={styles.commentAvatar}>
                          <img src={comment.profile || profileIcon} alt="profile" />
                        </div>
                        <div className={styles.commentContent}>
                          <strong>{comment.author}</strong>
                          <p className={comment.deleted ? styles.deletedComment : ''}>{comment.content}</p>
                          <div className={styles.commentActions}>
                            <span className={styles.commentTime}>{comment.time}</span>
                            {!comment.deleted && (
                              <button 
                                className={styles.commentReply}
                                onClick={() => handleReplyClick(comment.id)}
                              >
                                답글
                              </button>
                            )}
                            {comment.isMine && !comment.deleted && (
                              <button
                                type="button"
                                className={styles.deleteCommentBtn}
                                onClick={() => handleDeleteComment(comment.id)}
                                title="댓글 삭제"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                          
                          {/* 답글 입력창 */}
                          {replyingTo === comment.id && (
                            <div className={styles.replyInput}>
                              <div className={styles.commentAvatar}>
                                <img src={userProfileImage || profileIcon} alt="profile" />
                              </div>
                              <input
                                type="text"
                                placeholder="답글 달기"
                                value={replyText[comment.id] || ''}
                                onChange={(e) => setReplyText(prev => ({ ...prev, [comment.id]: e.target.value }))}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    handleReplySubmit(comment.id)
                                  }
                                }}
                              />
                              <button
                                className={styles.commentSubmit}
                                onClick={() => handleReplySubmit(comment.id)}
                                disabled={!replyText[comment.id]?.trim()}
                              >
                                등록
                              </button>
                            </div>
                          )}

                          {/* 답글 목록 */}
                          {comment.replies && comment.replies.length > 0 && (
                            <div className={styles.repliesList}>
                              {comment.replies.map((reply) => (
                                <div key={reply.id} className={styles.reply}>
                                  <div className={styles.commentAvatar}>
                                    <img src={reply.profile || profileIcon} alt="profile" />
                                  </div>
                                  <div className={styles.commentContent}>
                                    <strong>{reply.author}</strong>
                                    <p className={reply.deleted ? styles.deletedComment : ''}>{reply.content}</p>
                                    <div className={styles.commentActions}>
                                      <span className={styles.commentTime}>{reply.time}</span>
                                      {reply.isMine && !reply.deleted && (
                                        <button
                                          type="button"
                                          className={styles.deleteCommentBtn}
                                          onClick={() => handleDeleteComment(reply.id)}
                                          title="답글 삭제"
                                        >
                                          <i className="fas fa-trash"></i>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      ))
                    ) : null}
                    {postComments.length === 0 && (
                      <div className={styles.noComments}>아직 댓글이 없습니다. 첫 댓글을 작성해보세요!</div>
                    )}
                  </div>

                  {totalCommentPages > 1 && (
                    <div className={styles.commentPagination}>
                      <button
                        type="button"
                        className={styles.paginationBtn}
                        onClick={() => handleCommentPageChange(detailPostId, currentCommentPage - 1)}
                        disabled={currentCommentPage === 1}
                      >
                        이전
                      </button>
                      <span className={styles.pageIndicator}>{currentCommentPage} / {totalCommentPages}</span>
                      <button
                        type="button"
                        className={styles.paginationBtn}
                        onClick={() => handleCommentPageChange(detailPostId, currentCommentPage + 1)}
                        disabled={currentCommentPage === totalCommentPages}
                      >
                        다음
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <CreatePostModal
          isOpen={showEditPostModal}
          onClose={() => setShowEditPostModal(false)}
          onSave={handleUpdatePost}
          isEdit={true}
          initialData={{
            title: selectedPost?.title || '',
            content: selectedPost?.content || '',
          }}
        />
        <AlertModal
          isOpen={showAlert}
          onClose={() => setShowAlert(false)}
          message={alertMessage}
        />
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false)
            setPostToDelete(null)
          }}
          onConfirm={confirmDeletePost}
          title="게시글 삭제"
          message="정말 이 게시글을 삭제하시겠습니까?"
          confirmText="삭제"
          cancelText="취소"
          confirmButtonColor="#ef4444"
        />
        <ConfirmModal
          isOpen={showDeleteCommentConfirm}
          onClose={() => {
            setShowDeleteCommentConfirm(false)
            setCommentToDelete(null)
          }}
          onConfirm={confirmDeleteComment}
          title="댓글 삭제"
          message="댓글을 삭제하시겠습니까?"
          confirmText="삭제"
          cancelText="취소"
          confirmButtonColor="#ef4444"
        />
      </>
    )
  }

  // 메인 커뮤니티 페이지
  return (
    <>
      <Header />
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.icon}>
            <img src={communityIcon} alt="community" />
          </div>
          <h1>갤러리</h1>
        </div>

      {/* 탭 버튼 */}
      {activeTab === 'all' && (
        <div className={styles.tabButtons}>
          <button 
            className={styles.tabBtn}
            onClick={() => setShowCreatePostModal(true)}
          >
            <i className="fas fa-plus"></i>
            그림 업로드
          </button>
          <button 
            className={styles.tabBtn}
            onClick={() => setActiveTab('myPosts')}
          >
            <i className="fas fa-list"></i>
            내가 쓴글보기
          </button>
        </div>
      )}

      {activeTab === 'all' ? (
        <>
          {/* 인기 작품 섹션 */}
          <h2 className={styles.sectionTitle}>
            <span className={styles.fireIcon}>🔥</span> 주간 인기작품
          </h2>
          <div className={styles.hotSectionContainer}>
            <div className={styles.hotSection}>
              <button 
                className={styles.navBtn} 
                onClick={handlePrevSlide}
                disabled={hotSlideIndex === 0}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              
              <div 
                className={styles.hotCarousel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <div 
                  className={styles.hotGrid}
                  style={{
                    width: `${totalSlides * 100}%`,
                    transform: `translateX(-${hotSlideIndex * (100 / totalSlides)}%)`
                  }}
                >
                  {hotPosts.map((post) => (
                    <div 
                      key={post.id} 
                      className={styles.hotCard}
                      onClick={() => handlePostCardClick(post)}
                      style={{
                        flex: `0 0 calc((100% / ${totalSlides} - ${(itemsPerSlide - 1) * 20}px) / ${itemsPerSlide})`
                      }}
                    >
                      {post.rank <= 3 && (
                        <div className={`${styles.rankBadge} ${
                          post.rank === 1 ? styles.rankGold :
                          post.rank === 2 ? styles.rankSilver :
                          styles.rankBronze
                        }`}>
                          {post.rank}
                        </div>
                      )}
                      <div className={styles.cardImage}>
                        {post.imageUrl ? (
                          <img src={post.imageUrl} alt={post.title || '작품'} />
                        ) : (
                          <div className={styles.placeholder}></div>
                        )}
                      </div>

                      <div className={styles.cardInfo}>
                        <p className={styles.cardTitle}>{post.title}</p>
                        <div className={styles.cardStats}>
                          <span><i className="fas fa-user"></i> {post.author}</span>
                          <span><i className="fas fa-heart"></i> {post.likes}</span>
                          <span><i className="fas fa-eye"></i> {post.views}</span>
                          <span><i className="fas fa-comment"></i> {post.comments}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button 
                className={styles.navBtn}
                onClick={handleNextSlide}
                disabled={hotSlideIndex >= totalSlides - 1}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>

          {/* 최신 그림 섹션 */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>최신 그림</h2>
            <div className={styles.recentGrid}>
              {posts.length > 0 ? (
                posts.map((post) => (
                  <div 
                    key={post.id || post.articleId} 
                    className={styles.recentCard}
                    onClick={() => handlePostCardClick(post)}
                  >
                    <div className={styles.cardImage}>
                      {post.imageUrl ? (
                        <img src={post.imageUrl} alt={post.title || '작품'} />
                      ) : (
                        <div className={styles.placeholder}>
                          
                        </div>
                      )}
                    </div>
                    <div className={styles.cardInfo}>
                      <p className={styles.cardTitle}>{post.title || `작품 #${post.articleId || post.id}`}</p>
                      <div className={styles.cardStats}>
                        <span><i className="fas fa-user"></i> {post.authorName || post.author || post.nickname || '익명'}</span>
                        <span><i className="fas fa-heart"></i> {post.likeCount || post.likes || 0}</span>
                        <span><i className="fas fa-eye"></i> {post.viewCount || post.views || 0}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyState}>
                  <p>게시글이 없습니다.</p>
                </div>
              )}
            </div>

            <div ref={loaderRef} className={styles.scrollSentinel} />
          </div>
        </>
      ) : (
        <>
          {/* 내가 쓴 글 섹션 */}
          <div className={styles.section}>
            <div className={styles.myPostsHeader}>
              <h2 className={styles.sectionTitle}>내가 쓴 글</h2>
              <button 
                className={styles.backToAllBtn}
                onClick={() => setActiveTab('all')}
              >
                <i className="fas fa-arrow-left"></i>
                전체 보기
              </button>
            </div>
            <div className={styles.recentGrid}>
              {myPosts.length > 0 ? (
                myPosts.map((post) => (
                <div 
                  key={post.articleId || post.id} 
                  className={styles.recentCard}
                  onClick={() => handlePostCardClick(post)}
                  >
                    <div className={styles.cardImage}>
                      {post.imageUrl ? (
                        <img src={post.imageUrl} alt={post.title || '작품'} />
                      ) : (
                        <div className={styles.placeholder}>
                          
                        </div>
                      )}
                    </div>
                    <div className={styles.cardInfo}>
                      <p className={styles.cardTitle}>{post.title || `작품 #${post.articleId || post.id}`}</p>
                      <div className={styles.cardStats}>
                        <span><i className="fas fa-user"></i> {post.authorName || post.author || '익명'}</span>
                        <span><i className="fas fa-heart"></i> {post.likeCount || post.likes || 0}</span>
                        <span><i className="fas fa-eye"></i> {post.viewCount || post.views || 0}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyState}>
                  <p>작성한 게시글이 없습니다.</p>
                </div>
              )}
            </div>

            <div ref={myLoaderRef} className={styles.scrollSentinel} />
          </div>
        </>
      )}
      </div>

      {/* 게시글 작성 모달 */}
      <CreatePostModal
        isOpen={showCreatePostModal}
        onClose={() => setShowCreatePostModal(false)}
        onSave={handleCreatePost}
      />
      <CreatePostModal
        isOpen={showEditPostModal}
        onClose={() => setShowEditPostModal(false)}
        onSave={handleUpdatePost}
        isEdit={true}
        initialData={{
          title: selectedPost?.title || '',
          content: selectedPost?.content || '',
        }}
      />

        <AlertModal
          isOpen={showAlert}
          onClose={() => setShowAlert(false)}
          message={alertMessage}
        />
        <ConfirmModal
          isOpen={showDeleteCommentConfirm}
          onClose={() => {
            setShowDeleteCommentConfirm(false)
            setCommentToDelete(null)
          }}
          onConfirm={confirmDeleteComment}
          title="댓글 삭제"
          message="댓글을 삭제하시겠습니까?"
          confirmText="삭제"
          cancelText="취소"
          confirmButtonColor="#ef4444"
        />
      </>
    )
  }

export default CommunityPage
